<template>
  <div>
    <!-- Loading Screen -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-[#fafbfc] z-[99999]"
    >
      <LottieLoader :src="lottieJson" />
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="relative min-h-screen w-full overflow-hidden"
    >
      <!-- Two-tone Background -->
      <div class="absolute inset-0">
        <!-- Top Section - Background Image with Overlay (Banner Style) -->
        <div
          class="absolute top-0 left-0 right-0 h-[32%]"
          style="
            background-image: url('https://vamos-api-v2.sejadikopi.com/storage/images/login_bg.jpg');
            background-size: cover;
            background-position: center;
          "
        >
          <div class="absolute inset-0 bg-black/60" />
        </div>

        <!-- Wave SVG -->
        <svg
          class="absolute left-0 right-0 w-full"
          style="top: 30%; height: 60px"
          viewBox="0 0 1440 120"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            fill="#000000"
            fill-opacity="0.6"
            d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          />
        </svg>

        <!-- Bottom Section - Very Light Gray -->
        <div class="absolute bottom-0 left-0 right-0 h-[68%] bg-[#fafbfc]" />
      </div>

      <!-- Header with Back Button -->
      <header class="absolute top-0 left-0 right-0 z-30">
        <div class="px-6 pt-4 pb-4 flex justify-between items-center">
          <!-- Back Button -->
          <button
            aria-label="Kembali"
            class="p-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 active:scale-95 shadow-lg shadow-white/20 backdrop-blur-sm"
            @click="goBack"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 text-white drop-shadow-md"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Language Badge -->
          <div
            class="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20"
          >
            <span class="text-white font-semibold text-xs">ID</span>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="relative z-10 flex flex-col min-h-screen px-6 pt-12 pb-40">
        <!-- Welcome Text Section (Banner Area) -->
        <div class="flex flex-col items-center text-center py-8 mb-6">
          <h1 class="text-3xl font-bold text-white mb-2 drop-shadow-lg">
            Dine in
          </h1>
          <p class="text-sm text-white/90 drop-shadow-md">
            Lokasi meja Anda: {{ locationArea }}
          </p>
        </div>

        <!-- Input Section (White Area) -->
        <div class="w-full max-w-md mx-auto mb-8 flex-1">
          <!-- Card Container with Background -->
          <div
            class="bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] rounded-[32px] p-6 shadow-sm border border-white/60"
          >
            <!-- Card Title -->
            <div class="mb-6">
              <h2 class="text-xl font-bold text-[#2c3e50] mb-1">
                Nomor Meja
              </h2>
              <p class="text-sm text-[#7f8c8d]">
                Masukkan nomor meja yang tertera di meja Anda
              </p>
            </div>

            <!-- Input Card -->
            <div
              class="bg-white backdrop-blur-sm rounded-2xl shadow-md border border-gray-100 p-6 mb-4"
            >
              <input
                v-model="tableNumber"
                type="text"
                inputmode="numeric"
                placeholder="..."
                class="w-full py-6 px-4 rounded-xl border-2 border-[#ecf0f1] bg-[#f8f9fa] text-[#2c3e50] font-bold text-center text-2xl placeholder-[#7f8c8d]/50 focus:border-[#5dade2] focus:ring-2 focus:ring-[#f39c6b]/20 outline-none transition-all"
                maxlength="3"
                @keyup.enter="confirmTable"
                @input="validateTableInput"
              >
              <p
                v-if="tableError"
                class="text-red-500 text-sm mt-3 text-center font-medium"
              >
                {{ tableError }}
              </p>
            </div>

            <!-- Confirm Button Card -->
            <div
              class="bg-white backdrop-blur-sm rounded-2xl shadow-md border border-gray-100 p-6"
            >
              <button
                class="w-full bg-gradient-to-r from-[#397998] to-[#00557c] text-white py-4 px-5 rounded-xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!tableNumber.trim()"
                @click="confirmTable"
              >
                Konfirmasi Meja
              </button>
            </div>
            
            <!-- Info Text -->
            <p class="text-xs text-center text-[#7f8c8d] leading-relaxed mt-3">
              Pastikan nomor meja sudah benar sebelum melanjutkan
            </p>
          </div>
        </div>
      </main>

      <!-- Fixed Footer at Bottom -->
      <footer
        class="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-white via-white/95 to-transparent backdrop-blur-md border-t border-[#ecf0f1] shadow-2xl shadow-black/5"
      >
        <div class="w-full px-6 py-4">
          <div class="flex flex-col items-center gap-2.5">
            <!-- Professional Copyright Text -->
            <div class="text-center">
              <p class="text-xs font-medium text-[#34495e]">
                © 2025 Vamos Pool Cafe. All rights reserved.
              </p>
              <p class="text-xs text-[#7f8c8d] mt-0.5">
                Designed & Developed by
                <span class="font-semibold text-[#2c3e50]">Productivty Era</span>
              </p>
            </div>

            <!-- Logo Divider -->
            <div class="flex items-center gap-3">
              <img
                src="https://vamos-api-v2.sejadikopi.com/storage/Logo/vamos_logo.png"
                alt="Vamos Logo"
                class="h-5 w-auto opacity-60"
                draggable="false"
              >
              <div class="w-1 h-4 bg-[#bdc3c7]" />
              <img
                src="https://vamos-api-v2.sejadikopi.com/storage/Logo/logo_rpl.png"
                alt="RPL Logo"
                class="h-5 w-auto opacity-60"
                draggable="false"
              >
            </div>
          </div>
        </div>
      </footer>

      <!-- Confirmation Dialog -->
      <transition name="fade-popup">
        <div
          v-if="showConfirmation"
          class="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm p-6"
        >
          <div
            class="relative z-10 flex items-center justify-center w-full max-w-sm"
          >
            <div
              class="bg-white backdrop-blur-sm rounded-3xl p-8 shadow-2xl w-full text-center border border-gray-100"
            >
              <div class="mb-6">
                <div
                  class="w-16 h-16 bg-gradient-to-br from-[#397998] to-[#00557c] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                >
                  <svg
                    class="w-8 h-8 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-[#2c3e50] mb-2">
                  Konfirmasi Meja
                </h3>
                <p class="text-sm text-[#7f8c8d] leading-relaxed mb-4">
                  Apakah Anda yakin dengan pilihan meja berikut?
                </p>

                <!-- Lokasi Area -->
                <div class="text-sm font-semibold text-[#7f8c8d] mb-2">
                  LOKASI: {{ locationArea || "-" }}
                </div>

                <!-- Nomor Meja -->
                <div class="text-5xl font-bold text-[#3a79a0] mb-6">
                  {{ tableNumber }}
                </div>
              </div>

              <div class="space-y-3">
                <button
                  class="w-full bg-gradient-to-r from-[#397998] to-[#00557c] hover:shadow-lg text-white py-4 px-5 rounded-xl font-semibold text-base transition-all duration-300 shadow-md active:scale-95"
                  @click="proceedToMenu"
                >
                  Ya, Lanjutkan
                </button>

                <button
                  class="w-full bg-[#e8ecf1] hover:bg-[#dce2e8] text-[#2c3e50] py-4 px-5 rounded-xl font-semibold text-base transition-all duration-300 shadow-sm active:scale-95"
                  @click="cancelConfirmation"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import LottieLoader from "@/components/LottieLoader.vue";
import lottieJson from "../assets/loader.json";

export default {
  name: "SelectTable",
  components: { LottieLoader },
  data() {
    return {
      tableNumber: "",
      errorMsg: "",
      isLoading: true,
      lottieJson,
      tableError: "",
      locationArea: "",
      showConfirmation: false,
    };
  },
  mounted() {
    // Ambil session hash dari localStorage
    const sessionHash = localStorage.getItem("sessionHash");
    const orderMode = localStorage.getItem("orderMode");

    if (!sessionHash || sessionHash.length < 32) {
      this.$router.replace({ name: "SelectLocation" });
      return;
    }

    // SelectTable hanya untuk dine-in
    if (orderMode !== "dine-in") {
      this.$router.replace({ name: "SelectLocation" });
      return;
    }

    // Jika ada table number yang sudah disimpan
    const savedTable = localStorage.getItem("tableNumber");
    if (savedTable) {
      this.tableNumber = savedTable;
    }

    // Ambil location area dari localStorage
    this.locationArea = localStorage.getItem("locationArea") || "";

    // Loading simulation
    setTimeout(() => {
      this.isLoading = false;
    }, 1200);
  },
  methods: {
    validateTableInput() {
      // Hanya boleh angka, hapus karakter non-digit
      this.tableNumber = this.tableNumber.replace(/[^0-9]/g, "");
      this.tableError = "";

      // Validasi real-time - limit sampai 999
      if (this.tableNumber && parseInt(this.tableNumber) > 999) {
        this.tableNumber = "999";
      }
    },
    confirmTable() {
      const tableNum = this.tableNumber.trim();
      this.tableError = "";

      if (!tableNum) {
        this.tableError = "Nomor meja tidak boleh kosong!";
        return;
      }

      // Pastikan hanya angka
      if (!/^\d+$/.test(tableNum)) {
        this.tableError = "Nomor meja harus berupa angka!";
        return;
      }

      const num = parseInt(tableNum);
      if (num < 1) {
        this.tableError = "Nomor meja minimal 1!";
        return;
      }

      // Tampilkan popup konfirmasi
      this.showConfirmation = true;
    },
    proceedToMenu() {
      // Save table number and order type
      localStorage.setItem("tableNumber", this.tableNumber);
      localStorage.setItem("orderType", "DINE-IN");
      this.showConfirmation = false;

      const hash = localStorage.getItem("sessionHash");
      const tableId = this.tableNumber;

      this.$router.push({
        name: "MenuPage",
        params: { hash, tableId },
      });
    },
    cancelConfirmation() {
      this.showConfirmation = false;
    },
    closeError() {
      this.errorMsg = "";
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
/* Fade In Animation */
.animate-fade-in {
  animation: fadeIn 0.7s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Backdrop Blur */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.backdrop-blur-md {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.backdrop-blur-xl {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.backdrop-blur-2xl {
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

/* Sidebar Animations */
.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
  transition: opacity 0.3s ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
  opacity: 0;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  transform: translateX(-100%);
}

/* Popup Animations */
.fade-popup-enter-active {
  transition: opacity 0.25s ease-out;
}

.fade-popup-leave-active {
  transition: opacity 0.1s ease-in;
}

.fade-popup-enter-from {
  opacity: 0;
}

.fade-popup-leave-to {
  opacity: 0;
}

.fade-popup-enter-to,
.fade-popup-leave-from {
  opacity: 1;
}
</style>
